import { Badge } from '@/components/ui/badge';
import type { CvEvaluationResult } from '../../types';

/** CV reviewer screening result -> label and the pipeline column it moves the CV to. */
export const CV_EVALUATION_META: Record<
  CvEvaluationResult,
  { label: string; targetStage: string }
> = {
  PASS: {
    label: 'Pass',
    targetStage: 'Interview',
  },
  FAIL: {
    label: 'Failed',
    targetStage: 'Rejected',
  },
  PENDING: {
    label: 'Pending',
    targetStage: 'Talent Pool',
  },
};

/** `dd/mm/yyyy HH:mm` in local time. */
export const formatEvaluationTime = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  const pad = (part: number) => String(part).padStart(2, '0');

  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

const RESULT_BADGE_CLASS: Record<CvEvaluationResult, string> = {
  PASS: 'border-success/30 bg-success-muted text-success-muted-foreground',
  FAIL: 'border-error/30 bg-error-muted text-error-muted-foreground',
  PENDING:
    'border-violet-200 bg-violet-50 text-violet-600 dark:border-violet-300/30 dark:bg-violet-300/10 dark:text-violet-200',
};

/** Pass / Failed / Pending badge of a CV evaluation. */
export function CvEvaluationResultBadge({ result }: { result: CvEvaluationResult }) {
  return (
    <Badge className={`border px-2 py-0 text-xs font-semibold ${RESULT_BADGE_CLASS[result]}`}>
      {CV_EVALUATION_META[result].label}
    </Badge>
  );
}

/** List cell pill: whether a CV reviewer has evaluated the CV yet. */
export function EvaluationStatusPill({ evaluated }: { evaluated: boolean }) {
  return (
    <Badge
      className={`gap-1.5 rounded-full border px-2.5 text-xs font-semibold ${
        evaluated
          ? 'border-success/30 bg-success-muted text-success-muted-foreground'
          : 'border-warning/30 bg-warning-muted text-warning-muted-foreground'
      }`}
    >
      <span
        className={`size-1.5 rounded-full ${evaluated ? 'bg-success' : 'bg-warning'}`}
        aria-hidden
      />
      {evaluated ? 'Evaluated' : 'Not evaluated'}
    </Badge>
  );
}
